import { StripeInterval } from '../../../reducers/subscription.slice'
import { StripeProduct } from '../../../actions/settings/billingActions'
import {
  fetchStripePlans,
  getAnnualCommitmentPriceIdFromProductId,
} from '../../../constants/pricingConstants'
import { centsToCurrency, dollarsToCurrency } from '@/utils/currencyHelpers'
import { ADDITIONAL_1040_COST } from '../../Taxes/AnnualTaxes/constants'
import { DateTime } from 'luxon'
import { ProductFeatureId } from '@/actions/permissionActions'
import { ReactNode } from 'react'
import { Text } from '../../../components/BaseComponents'

export interface PlanDetail {
  text: string
  tooltip?: ReactNode
}

export interface PlanDetailMap {
  [key: string]: PlanDetail
}

const currentYear = DateTime.now().year
const filingYear = currentYear + 1

const bookkeepingDetails: PlanDetailMap = {
  monthlyBookkeepingServices: {
    text: 'Monthly done-for-you bookkeeping',
    tooltip:
      'Complete monthly bookkeeping service where our platform and your dedicated bookkeeping team keeps your books up-to-date, organized, and accurate.',
  },
  connectMultipleBankAccounts: {
    text: 'Connect multiple business bank accounts',
    tooltip:
      'Easily sync all your business checking, savings, and credit card accounts via a secure integration for complete financial tracking in one place.',
  },
  bookkeepingProfessionalsSpecialized: {
    text: 'Dedicated bookkeeping team',
    tooltip:
      'Your dedicated bookkeeping team reviews your transactions that are automatically categorized by the Heard platform to ensure accuracy and handles account reconciliation for your practice.',
  },
  taxReadyBooks: {
    text: 'Year-end tax ready books',
    tooltip:
      'Your books are organized and prepared for seamless annual tax filing within the Heard platform (must upgrade to essential or premium plan if Heard tax prep and filing is required or work with outside tax preparer).',
  },
  taxDeductionTracking: {
    text: 'Tax deduction tracking',
    tooltip:
      'We categorize and track all business expenses to ensure you capture every eligible tax deduction.',
  },
}

const cubkDetails: PlanDetailMap = {
  cubkReconciliation: {
    text: 'Complete reconciliation of past transactions',
    tooltip: 'Complete reconciliation of past transactions',
  },
  cubkProfessionalReview: {
    text: 'Professional review of deductible expenses',
    tooltip: 'Professional review of deductible expenses',
  },
  cubkFinancialInsights: {
    text: 'Year-to-date financial insights and reporting',
    tooltip: 'Year-to-date financial insights and reporting',
  },
}

const reportingDetails: PlanDetailMap = {
  monthlyFinancialReports: {
    text: 'Monthly income, expense, and profit reports and tracking',
    tooltip:
      "Get easy-to-understand financial reports showing your practice's performance, cash flow, and profitability trends over time.",
  },
  pnlStatements: {
    text: 'Profit and loss statements',
    tooltip:
      'Get professional P&L statements that clearly show your income, expenses, and net profit for tax purposes and business planning.',
  },
  allocationGuide: {
    text: 'Profit allocation guide',
    tooltip:
      'A planning tool that helps you determine how to distribute your profits across taxes, savings, retirement and more based on your financial situation.',
  },
  incomeAndGoalTracking: {
    text: 'Real-time income tracking and goal monitoring',
    tooltip:
      'Track your income in real-time and monitor progress toward an annual income goal throughout the year.',
  },
}

const supportDetails: PlanDetailMap = {
  assistedOnboarding: {
    text: 'Assisted onboarding with specialists',
    tooltip:
      'Group and one-on-one setup help from our team to get your account setup correctly from day one.',
  },
  unlimitedAppMessaging: {
    text: 'Unlimited messaging support for bookkeeping and general platform questions',
    tooltip:
      'Get help with day-to-day bookkeeping, platform features, and general business questions. Tax support not included, must upgrade to premium or essential plan.',
  },
  liveQnA: {
    text: 'Live support Q&A sessions',
    tooltip:
      'Group webinar sessions where you can ask questions and learn best practices for managing your Heard account.',
  },
  phoneSupport: {
    text: 'Unlimited messaging and phone support for all financial matters',
    tooltip:
      'Full access to our tax and accounting specialists via both messaging and phone for bookkeeping, tax, and financial questions. Call into our live phone support anytime from Monday-Friday, 8 AM to 8 PM EST. It is most convenient for quick questions between client sessions.',
  },
}

const taxDetails: PlanDetailMap = {
  quarterlyTaxEstimates: {
    text: 'Quarterly tax estimates and payment guidance',
    tooltip:
      'Every quarter, we calculate your estimated state and federal tax payments, remind you when to pay so you never miss a deadline, and provide step-by-step guidance on how to pay.',
  },
  sCorpElectionGuidance: {
    text: 'S corporation election guidance and support',
    tooltip:
      "We'll assess if S corp is right for your practice via a personalized readiness report. If S corp makes sense, we’ll file Form 2553 for you to elect your therapy practice to be taxed as an S corporation by the IRS. We help you meet S corporation requirements: Incorporation and EIN changes.",
  },
  personalTaxPrep: {
    text: 'Personal income tax prep and filing',
    tooltip:
      "Our team files your annual state and federal personal income tax return (1040). Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings. If needed, we prepare and file your joint tax return with your spouse. We cannot file for the spouse if they are filing separately.",
  },
  businessTaxPrep: {
    text: 'Business income tax prep and filing',
    tooltip:
      "Our team files your annual state and federal business income tax return (Form 1120S). Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.",
  },
  documentManagement: {
    text: 'Document management and automated filing reminders',
    tooltip:
      "Secure storage for all your tax documents with automatic reminders when it's time to file quarterly estimates and annual returns.",
  },
  freeExtensions: {
    text: 'Free tax extension filing support (as needed)',
    tooltip:
      "If you need more time to file your taxes come annual tax season, we'll handle filing extensions at no additional cost to keep you compliant with deadlines. We will establish a deadline for when you must file an extension request by.",
  },
}

const sCorpComplianceDetails: PlanDetailMap = {
  sCorpComplianceGuidance: {
    text: 'S corporation compliance with salary and distribution guidance',
    tooltip: (
      <Text>
        We make sure you stay compliant and avoid fees or penalties from the
        IRS. We audit your books and advise you if you aren&rsquo;t compliant
        with S corporation tax regulations and provide resources for you to keep
        on hand so you understand how to remain in compliance.
        {
          <>
            <br />
            <br />
          </>
        }{' '}
        We&rsquo;ll review your reasonable salary to make sure you&rsquo;re
        paying yourself the right amount.Setting a reasonable salary that is not
        too high or too low can affect your practice&rsquo;s profits and overall
        business success.
      </Text>
    ),
  },
  payrollSetup: {
    text: 'Payroll setup and management',
    tooltip:
      'We’ve teamed up with Gusto to provide stress-free payroll, embedded right in your Heard dashboard so you can stay compliant as an S corp. Payroll for yourself is included ($45/mo value). Additional employees will cost $6/mo per employee.',
  },
  accountablePlanGuidance: {
    text: 'Accountable plan creation and compliance',
    tooltip:
      'We create an IRS-compliant Accountable Plan allowing your S-Corp to reimburse home office, mileage, cell phone, and internet expenses without creating taxable income. Includes policy documentation, corporate resolutions, and compliance guidance.',
  },
}

const groupPracticeDetails: PlanDetailMap = {
  payrollSetup: {
    text: 'Payroll setup and management for yourself and employees',
    tooltip:
      'We’ve teamed up with Gusto to provide stress-free payroll, embedded right in your Heard dashboard so you can stay compliant as an S corp and easily pay yourself and employees. Payroll for yourself is included if you are an S corp ($45/mo value). Additional employees will cost $6/mo per employee.',
  },
}

// Stores the entire collection of "selling points" in a single object
const PlanDetails: PlanDetailMap = {
  annualBusinessTaxPrepSoloScorp: {
    text: `${currentYear} Annual business income tax prep and filing (filed in ${filingYear})`,
    tooltip:
      "Our team files your annual state and federal business income tax return. Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.",
  },
  annualPersonalTaxPrepSCorp: {
    text: `${currentYear} Annual personal income tax prep and filing (filed in ${filingYear})`,
    tooltip: `Our team files your annual state and federal personal income tax return ($${ADDITIONAL_1040_COST} value). Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.`,
  },
  annualIncomeTaxPrepSoleProp: {
    text: `${currentYear} Annual personal income tax prep and filing (filed in ${filingYear})`,
    tooltip:
      "Our team files your annual state and federal personal income tax return. Because we also do your bookkeeping, we're able to maximize your deductions which translates to more savings.",
  },
  quarterlyTaxEstimates: {
    text: 'Quarterly tax estimates',
    tooltip:
      'Every quarter, we calculate your estimated payments and remind you when to pay so you never miss a deadline.',
  },
  dedicatedTaxProfessional: {
    text: `Dedicated tax expert who supports you through your ${currentYear} tax filing`,
    tooltip:
      'Work directly with your dedicated tax expert during your tax filing to maximize your deductions.',
  },
  paidTaxAdvisory: {
    text: 'Personalized Tax Consultations (Add on)',
    tooltip:
      'You’ll be able to book 30 and 60 minute conversations with your tax preparer during your tax season for an additional fee.',
  },
  monthlyBookkeepingServices: {
    text: 'Monthly bookkeeping services',
    tooltip: null,
  },
  bookkeepingProfessionalsSpecialized: {
    text: 'Bookkeeping professionals specialized in therapy practice',
    tooltip:
      'Our only focus is therapists. That means your dedicated bookkeeping team and tax professionals are experts in therapy practices and all of the nuances that come with running one.',
  },
  unlimitedAppMessaging: {
    text: 'Unlimited in-app messaging support',
    tooltip:
      "You can message our support team through the Heard app 24/7 and we'll get back to you in 1-2 business days.",
  },
  financialReportsAndProfitAllocation: {
    text: 'Financial reports and profit allocation guide',
    tooltip:
      'We share monthly financial reports and allocation guide to help you confidently allocate your profit. Financial reports are automatically generated once bookkeeping is complete, with a summary of your cash flow and profit and loss. This helps you better understand your practice finances.',
  },
  expenseBreakdown: {
    text: 'Expense breakdown',
    tooltip: null,
  },
  singlePlaceToKeepDocuments: {
    text: 'A single place to keep important documents and receipts',
    tooltip: null,
  },
  payrollIncluded: {
    text: 'Integrated payroll management via Gusto',
    tooltip:
      'We’ve teamed up with Gusto to provide stress-free payroll, embedded right in your Heard dashboard. Payroll for yourself is included ($45/mo value). Additional employees will cost $6/mo per employee.',
  },
  payrollAddOn: {
    text: 'Integrated payroll management via Gusto (Add on)',
    tooltip:
      'We’ve teamed up with Gusto to provide stress-free payroll, embedded right in your Heard dashboard. You’ll be billed $39/mo + $6/mo per employee.',
  },
  sCorpCompliance: {
    text: 'S corporation compliance',
    tooltip:
      "We make sure you stay compliant and avoid fees or penalties from the IRS. We audit your books and advise you if you aren't compliant with S corporation tax regulations and provide resources for you to keep on hand so you understand how to remain in compliance.",
  },
  reasonableSalaryDetermination: {
    text: 'Reasonable salary determination',
    tooltip:
      "Throughout the year, we'll review your reasonable salary to make sure you're paying yourself the right amount. Setting a reasonable salary that is not too high or too low can affect your practice's profits and overall business success.",
  },
  sCorpElection: {
    text: 'S corporation election',
    tooltip:
      "We'll file Form 2553 for you to elect your therapy practice to be taxed as an S corporation by the IRS. We help you meet S corporation requirements: Incorporation and EIN changes. You can deduct certain business expenses, such as salaries, wages, bonuses, rent, utilities, and supplies. Deducting these expenses can reduce your taxable income and lower your taxes.",
  },
  sCorpEligibilityAssessment: {
    text: 'S corporation readiness report',
    tooltip:
      "We'll create a personalized readiness report to determine if it is a good time to elect S corporation, including an overview of the tax savings and associated costs.",
  },
  cubkReconciliation: {
    text: 'Complete reconciliation of past transactions',
    tooltip: 'Complete reconciliation of past transactions',
  },
  cubkProfessionalReview: {
    text: 'Professional review of deductible expenses',
    tooltip: 'Professional review of deductible expenses',
  },
  cubkFinancialInsights: {
    text: 'Year-to-date financial insights and reporting',
    tooltip: 'Year-to-date financial insights and reporting',
  },
  basicPlanTaxesAccountingDocs: {
    text: 'Accounting documents for easy tax filing with an outside preparer',
  },
  basicPlanIdentifyTaxDeductions: {
    text: 'Identification of tax deductible expenses via dedicated bookkeeping team',
  },
}

// Construct arrays of plan details for each of the 4 sections - Tax, Bookkeeping, Budgeting & Tracking, S-Corporations

// Tax
export const getTaxSectionDetails = ({
  isScorp,
  isBasicPlan,
  basicPlanSearchParamValue,
  permissions,
  isStarterPlanEnabled,
}: {
  isScorp: boolean
  isBasicPlan: boolean
  basicPlanSearchParamValue: string | null
  permissions: ProductFeatureId[]
  isStarterPlanEnabled: boolean
}) => {
  const details: PlanDetail[] = []
  if (isBasicPlan) {
    if (basicPlanSearchParamValue !== 'basic') {
      details.push(PlanDetails.quarterlyTaxEstimates)
    }
    return details
  }

  if (isStarterPlanEnabled) {
    if (permissions.includes(ProductFeatureId.quarterlyTaxes)) {
      details.push(taxDetails.quarterlyTaxEstimates)
    }
    if (permissions.includes(ProductFeatureId.annualTaxes)) {
      if (permissions.includes(ProductFeatureId.sCorpEligibilitySupport)) {
        details.push(taxDetails.sCorpElectionGuidance)
      }
      details.push(taxDetails.personalTaxPrep)
      if (permissions.includes(ProductFeatureId.annualTaxesBusinessFiling)) {
        details.push(taxDetails.businessTaxPrep)
      }
      details.push(taxDetails.documentManagement)
      details.push(taxDetails.freeExtensions)
    }
    return details
  }
  if (isScorp) {
    details.push(PlanDetails.annualBusinessTaxPrepSoloScorp)
    details.push(PlanDetails.annualPersonalTaxPrepSCorp)
  } else {
    details.push(PlanDetails.annualIncomeTaxPrepSoleProp)
  }
  details.push(
    PlanDetails.quarterlyTaxEstimates,
    PlanDetails.dedicatedTaxProfessional,
    PlanDetails.paidTaxAdvisory
  )

  return details
}

// Bookkeeping
export const getBookkeepingSectionDetails = ({
  isBasicPlan,
  isStarterPlanEnabled,
}: {
  isBasicPlan: boolean
  isStarterPlanEnabled: boolean
}) => {
  if (isBasicPlan) {
    return [
      PlanDetails.basicPlanTaxesAccountingDocs,
      PlanDetails.basicPlanIdentifyTaxDeductions,
    ]
  }
  if (isStarterPlanEnabled) {
    return [
      bookkeepingDetails.monthlyBookkeepingServices,
      bookkeepingDetails.connectMultipleBankAccounts,
      bookkeepingDetails.bookkeepingProfessionalsSpecialized,
      bookkeepingDetails.taxReadyBooks,
      bookkeepingDetails.taxDeductionTracking,
    ]
  }
  return [
    PlanDetails.monthlyBookkeepingServices,
    PlanDetails.bookkeepingProfessionalsSpecialized,
    PlanDetails.unlimitedAppMessaging,
  ]
}

// Catch up bookkeeping
export const generalCUBKDetails = [
  cubkDetails.cubkReconciliation,
  cubkDetails.cubkProfessionalReview,
  cubkDetails.cubkFinancialInsights,
]

// Budgeting & Tracking
export const getBudgetingSectionDetails = ({
  isGroup,
  isSCorp,
}: {
  isGroup: boolean
  isSCorp: boolean
}) => {
  const details: PlanDetail[] = [
    PlanDetails.financialReportsAndProfitAllocation,
    PlanDetails.expenseBreakdown,
    PlanDetails.singlePlaceToKeepDocuments,
  ]
  if (!isSCorp && isGroup) {
    details.push(PlanDetails.payrollAddOn)
  }
  return details
}

export const getReportingSectionDetails = () => {
  return [
    reportingDetails.monthlyFinancialReports,
    reportingDetails.pnlStatements,
    reportingDetails.allocationGuide,
    reportingDetails.incomeAndGoalTracking,
  ]
}

// S Corporation
export const getSCorpSectionDetails = ({
  isSCorp,
  isBasicPlan,
  permissions,
  isStarterPlanEnabled,
}: {
  isSCorp: boolean
  isBasicPlan: boolean
  permissions: ProductFeatureId[]
  isStarterPlanEnabled: boolean
}) => {
  if (isBasicPlan) {
    return []
  }
  if (isStarterPlanEnabled) {
    if (permissions.includes(ProductFeatureId.sCorpComplianceSupport)) {
      return [
        sCorpComplianceDetails.sCorpComplianceGuidance,
        sCorpComplianceDetails.payrollSetup,
        sCorpComplianceDetails.accountablePlanGuidance,
      ]
    }
    return []
  }
  if (isSCorp) {
    return [
      PlanDetails.payrollIncluded,
      PlanDetails.sCorpCompliance,
      PlanDetails.reasonableSalaryDetermination,
    ]
  }
  return [PlanDetails.sCorpEligibilityAssessment, PlanDetails.sCorpElection]
}

export const getGroupPracticeSectionDetails = ({
  isStarterPlanEnabled,
  permissions,
}: {
  isStarterPlanEnabled: boolean
  permissions: ProductFeatureId[]
}) => {
  if (
    isStarterPlanEnabled &&
    permissions.includes(ProductFeatureId.groupPracticeSupport)
  ) {
    return [groupPracticeDetails.payrollSetup]
  }
  return []
}

export const getSupportSectionDetails = ({
  isStarterPlanEnabled,
  permissions,
  extendedSupport,
}: {
  isStarterPlanEnabled: boolean
  permissions: ProductFeatureId[]
  extendedSupport: boolean
}) => {
  const details: PlanDetail[] = []
  if (isStarterPlanEnabled) {
    if (permissions.includes(ProductFeatureId.phoneSupport)) {
      details.push(supportDetails.phoneSupport)
      if (extendedSupport) {
        details.push(supportDetails.assistedOnboarding)
        details.push(supportDetails.liveQnA)
      }
      return details
    }
    details.push(
      supportDetails.assistedOnboarding,
      supportDetails.unlimitedAppMessaging,
      supportDetails.liveQnA
    )
  }
  return details
}

export const getProductByInterval = (
  products: StripeProduct[],
  interval: StripeInterval
) =>
  products.find(
    (product) => product.default_price.recurring.interval === interval
  )

// This shape is compatible with the new StripeProductCard component
// We cannot fetch Basic Plans from stripe b/c their shape on Stripe is inconsistent
// with other primary products. Sahil says we're planning to sunset Basic Plans come 2024,
// so we should keep pricing constants related to this product until then
export const fetchBasicProductsV2 = () => {
  const stripePlans = fetchStripePlans()

  const baseProduct: StripeProduct = {
    id: '',
    name: '',
    object: 'product',
    active: false,
    attributes: [],
    created: 0,
    description: '',
    features: [],
    images: [],
    livemode: false,
    metadata: {},
    statement_descriptor: '',
    tax_code: '',
    unit_label: '',
    updated: 0,
    url: '',
    default_price: {
      id: '',
      object: 'price',
      active: true,
      billing_scheme: '',
      created: 0,
      currency: 'USD',
      livemode: false,
      metadata: null,
      nickname: '',
      product: '',
      tax_behavior: 'inclusive',
      tiers_mode: null,
      transform_quantity: null,
      type: 'one_time',
      unit_amount: 0,
      unit_amount_decimal: '',
      recurring: {
        interval: StripeInterval.month,
        aggregate_usage: null,
        interval_count: 0,
        trial_period_days: null,
        usage_type: 'licensed',
      },
    },
  }

  const result = {
    BASIC_SOLO_PRODUCTS: [
      // solo monthly
      {
        ...baseProduct,
        name: 'Basic Plus Plan (Monthly)',
        default_price: {
          ...baseProduct.default_price,
          id: stripePlans.basic_solo.monthly.priceId,
          unit_amount: dollarsToCurrency(stripePlans.basic_solo.monthly.amount)
            .intValue,
          recurring: {
            ...baseProduct.default_price.recurring,
            interval: StripeInterval.month,
          },
        },
      },
      // solo annual product
      {
        ...baseProduct,
        name: 'Basic Plus Plan (Annual)',
        default_price: {
          ...baseProduct.default_price,
          id: stripePlans.basic_solo.annual.priceId,
          unit_amount: dollarsToCurrency(stripePlans.basic_solo.annual.amount)
            .intValue,
          recurring: {
            ...baseProduct.default_price.recurring,
            interval: StripeInterval.year,
          },
        },
      },
    ],

    BASIC_GROUP_PRODUCTS: [
      // group monthly
      {
        ...baseProduct,
        name: 'Basic Plus Plan (Monthly)',
        default_price: {
          ...baseProduct.default_price,
          id: stripePlans.basic_group.monthly.priceId,
          unit_amount: dollarsToCurrency(stripePlans.basic_group.monthly.amount)
            .intValue,
          recurring: {
            ...baseProduct.default_price.recurring,
            interval: StripeInterval.month,
          },
        },
      },
      // group annual
      {
        ...baseProduct,
        name: 'Basic Plus Plan (Annual)',
        default_price: {
          ...baseProduct.default_price,
          id: stripePlans.basic_group.annual.priceId,
          unit_amount: dollarsToCurrency(stripePlans.basic_group.annual.amount)
            .intValue,
          recurring: {
            ...baseProduct.default_price.recurring,
            interval: StripeInterval.year,
          },
        },
      },
    ],
    BASIC_NO_QTE_PRODUCTS: [
      // solo monthly
      {
        ...baseProduct,
        name: 'Basic Plan (Monthly)',
        default_price: {
          ...baseProduct.default_price,
          id: stripePlans.basic_no_qte.monthly.priceId,
          unit_amount: dollarsToCurrency(
            stripePlans.basic_no_qte.monthly.amount
          ).intValue,
          recurring: {
            ...baseProduct.default_price.recurring,
            interval: StripeInterval.month,
          },
        },
      },
      // solo annual
      {
        ...baseProduct,
        name: 'Basic Plan (Annual)',
        default_price: {
          ...baseProduct.default_price,
          id: stripePlans.basic_no_qte.annual.priceId,
          unit_amount: dollarsToCurrency(stripePlans.basic_no_qte.annual.amount)
            .intValue,
          recurring: {
            ...baseProduct.default_price.recurring,
            interval: StripeInterval.year,
          },
        },
      },
    ],
  }

  return result
}

export const productPriceAsCurrency = ({
  hasAnnualCommitment,
  product,
}: {
  hasAnnualCommitment: boolean
  product?: StripeProduct
}) => {
  if (hasAnnualCommitment && product?.id) {
    // skipcq: JS-0099, TODO: Clean up after annual commitment logic after expieriment
    // this is a hack to get the annual commit price without changing the eligible product api logic
    const annualCommitmentPrice = getAnnualCommitmentPriceIdFromProductId(
      product.id
    )
    if (annualCommitmentPrice) {
      return centsToCurrency(annualCommitmentPrice.amountInCents)
    }
  }
  return centsToCurrency(product?.default_price.unit_amount) || 0
}
